import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, ConfirmDialog, Field, Select, Spinner } from '@wundo/ui';
import { ApiError } from '@wundo/client/api.ts';
import { OrganisationPicker } from '@/components/pickers/OrganisationPicker.tsx';
import { UserPicker } from '@/components/pickers/UserPicker.tsx';
import { type UserOption, userOptionLabel } from '@/components/pickers/userOption.ts';
import { useActiveOrganisation } from '@/lib/activeOrganisation.ts';
import { useResetOnChange } from '@/lib/useResetOnChange.ts';
import { sendBroadcast } from '@/lib/broadcast.ts';
import type { BroadcastResponse, Template } from '@/types/broadcast.ts';
import { Notice } from './Notice.tsx';
import { SendResultDialog } from './SendResultDialog.tsx';
import type { TemplatesQuery } from './useBroadcastTemplates.ts';
import { ReadingColumn } from '@/components/ReadingColumn.tsx';

export interface SendTabProps {
  templates: TemplatesQuery;
  /** A template named in the URL, so a link can open this tab ready to send. */
  preselected?: string;
}

/**
 * Pick a template, pick the people, send it. The send cannot be taken back and
 * one rate-limited recipient fails the whole run, so the recipients are named
 * again in a confirmation before anything leaves.
 */
export function SendTab({ templates, preselected }: SendTabProps) {
  const queryClient = useQueryClient();

  /* null while nobody has touched the field, which is what lets the URL answer for it. */
  const [chosen, setChosen] = useState<string | null>(null);
  /* Both start over when the header's organisation changes: its people are another directory. */
  const headerOrganisation = useActiveOrganisation().organisation;
  const headerKey = headerOrganisation?.id ?? '';
  const [organisation, setOrganisation] = useState(headerOrganisation);
  const [recipients, setRecipients] = useState<UserOption[]>([]);
  useResetOnChange(headerKey, () => {
    setOrganisation(headerOrganisation);
    setRecipients([]);
  });
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<BroadcastResponse | null>(null);

  const available = templates.templates;

  /*
   * A link may name the template, "send this one". It only answers while the
   * field is untouched and the template still exists; after that the choice on
   * screen is the sender's.
   */
  const templateName =
    chosen ??
    (preselected !== undefined &&
    available.some((template) => template.name === preselected)
      ? preselected
      : NO_TEMPLATE);

  const send = useMutation({
    mutationFn: () =>
      sendBroadcast({
        user_ids: recipients.map((user) => user.id),
        template_name: templateName,
      }),
    onSuccess: async (response) => {
      setConfirming(false);
      setResult(response);
      await queryClient.invalidateQueries({ queryKey: ['broadcasts'] });

      /* A clean run is finished with; a partial one keeps the form to look at. */
      if (response.failed === 0) {
        setRecipients([]);
        setChosen(NO_TEMPLATE);
      }
    },
    /*
     * A refused request is a failed broadcast, and it is reported in the same
     * panel a partial one is: the sender asked one question — did it go? — and
     * there is one place that answers it.
     */
    onError: (failure) => {
      setConfirming(false);
      setResult({
        total: recipients.length,
        sent: 0,
        failed: recipients.length,
        errors: [
          (failure instanceof ApiError ? failure.detail : null) ??
            (failure instanceof Error ? failure.message : 'Unknown error'),
        ],
      });
    },
  });

  const canSend = recipients.length > 0 && templateName !== NO_TEMPLATE;

  if (templates.isPending) {
    return <Spinner />;
  }

  return (
    <ReadingColumn className='flex flex-col gap-5'>
      {available.length === 0 && (
        <Notice>
          <p>
            <strong>No templates available.</strong> Create one on the Templates tab
            before sending a broadcast.
          </p>
        </Notice>
      )}

      <TemplateField templates={available} value={templateName} onChange={setChosen} />

      <Field label='Filter by Organisation (Optional)'>
        {(control) => (
          <OrganisationPicker
            id={control.id}
            clearable
            placeholder='Select organisation...'
            value={organisation}
            onChange={(next) => {
              setOrganisation(next);
              /* Another organisation is another directory: the picks no longer apply. */
              setRecipients([]);
            }}
          />
        )}
      </Field>

      <Field
        label='Select Recipients'
        required
        description='Select users to send the broadcast to. Each one receives it on their own channel.'
      >
        {(control) => (
          <UserPicker
            id={control.id}
            multiple
            placeholder='Search and select users...'
            organisationId={organisation?.id}
            value={recipients}
            onChange={setRecipients}
          />
        )}
      </Field>

      {recipients.length > 0 && (
        <p className='text-sm text-ink-secondary'>
          <strong>{recipients.length}</strong> user{recipients.length !== 1 ? 's' : ''}{' '}
          selected
        </p>
      )}

      <Notice title='Important Broadcast Rules'>
        <ul className='list-inside list-disc'>
          <li>Users can only receive one broadcast message per hour</li>
          <li>If any user is rate-limited, the entire broadcast will fail</li>
        </ul>
      </Notice>

      <div className='flex justify-end border-t border-line pt-4'>
        <Button
          disabled={!canSend}
          loading={send.isPending}
          onClick={() => setConfirming(true)}
        >
          Send Broadcast
        </Button>
      </div>

      <ConfirmDialog
        open={confirming}
        onOpenChange={(open) => !open && setConfirming(false)}
        title='Send this broadcast?'
        description={`"${templateName}" goes to ${recipients.length} user${recipients.length !== 1 ? 's' : ''}: ${recipients.map(userOptionLabel).join(', ')}. A sent broadcast cannot be taken back.`}
        confirmLabel='Send'
        cancelLabel='Cancel'
        onConfirm={async () => {
          await send.mutateAsync().catch(() => undefined);
        }}
      />

      <SendResultDialog result={result} onClose={() => setResult(null)} />
    </ReadingColumn>
  );
}

const NO_TEMPLATE = '';

interface TemplateFieldProps {
  templates: Template[];
  /** The chosen template's name, or `NO_TEMPLATE`. */
  value: string;
  onChange: (name: string) => void;
}

function TemplateField({ templates, value, onChange }: TemplateFieldProps) {
  const selected = templates.find((template) => template.name === value);

  return (
    <>
      <Field label='Select Template' required>
        {(control) => (
          <Select
            {...control}
            wrapperClassName='w-full'
            className='w-full'
            disabled={templates.length === 0}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            options={[
              { value: NO_TEMPLATE, label: 'Choose a template...' },
              ...templates.map((template) => ({
                value: template.name,
                label: `${template.name} (${template.language.toUpperCase()})`,
              })),
            ]}
          />
        )}
      </Field>

      {selected !== undefined && (
        <Card className='flex flex-col gap-3'>
          <div className='flex items-center gap-2'>
            <h3 className='text-md font-bold text-ink'>Template Preview</h3>
            <span className='text-sm text-ink-muted'>
              {selected.language.toUpperCase()}
            </span>
          </div>
          <p className='rounded-md bg-fill-faint p-3.5 text-sm whitespace-pre-wrap text-ink'>
            {selected.body}
          </p>
          <p className='text-xs text-ink-muted'>{selected.body.length} characters</p>
        </Card>
      )}
    </>
  );
}
